define(["require", "exports", "preact/jsx-runtime", "@os/os-communication-interface/lib/runtime/FrameworkPluginRuntime", "@os/os-components/lib/OsHooks/useTranslationFor", "preact/hooks", "../utils/analytics", "../utils/environment", "../utils/agent", "../utils/pluginUrl", "../utils/pluginContext", "./occNotificationCenterBell.scss", "oj-c/button", "oj-c/drawer-popup"], function (require, exports, jsx_runtime_1, FrameworkPluginRuntime_1, useTranslationFor_1, hooks_1, analytics_1, environment_1, agent_1, pluginUrl_1, pluginContext_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.OccNotificationCenterBell = void 0;
    const runtimeLabel = "OCC_NOTIFICATION_CENTER";
    const frameId = "occNotificationCenterFrame";
    // The 13.3.0-rc.0 runtime can store the iframe WindowProxy before navigation;
    // align it with the actual handshake source before the runtime listener runs.
    const alignRuntimeChildWindow = (runtime, source) => {
        runtime._childWindow = source;
    };
    const OccNotificationCenterBell = ({ accessToken, accountId, custAccountId, environment, frameworkPlugins, language, navigate, notificationCenterPlugin, notificationItems, refreshNotifications, }) => {
        var _a;
        const [isDrawerOpen, setIsDrawerOpen] = (0, hooks_1.useState)(false);
        const [windowHeight, setWindowHeight] = (0, hooks_1.useState)("100%");
        const frameRef = (0, hooks_1.useRef)(null);
        const drawerTitle = (0, useTranslationFor_1.useTranslationFor)("notifications.drawerTitle");
        const closeButtonLabel = (0, useTranslationFor_1.useTranslationFor)("close");
        const notificationCenterUrl = (0, hooks_1.useMemo)(() => (0, pluginUrl_1.appendPluginSearchParams)(notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.url), [notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.url]);
        const allowedOrigins = (0, hooks_1.useMemo)(() => {
            var _a;
            if (!((_a = notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.options) === null || _a === void 0 ? void 0 : _a.httpHeaderAuth) || !notificationCenterUrl) {
                return undefined;
            }
            return [new URL(notificationCenterUrl, window.location.origin).origin];
        }, [(_a = notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.options) === null || _a === void 0 ? void 0 : _a.httpHeaderAuth, notificationCenterUrl]);
        const runtimeAccountId = (0, pluginContext_1.resolvePluginAccountId)(environment, accountId);
        const runtimeContext = (0, hooks_1.useMemo)(() => ({
            accessToken,
            accountId: runtimeAccountId !== null && runtimeAccountId !== void 0 ? runtimeAccountId : undefined,
            custAccountId: custAccountId !== null && custAccountId !== void 0 ? custAccountId : undefined,
            frameworkPlugins,
            language,
            notificationItems,
            parentInnerWidth: window.innerWidth,
            parentInnerHeight: window.innerHeight,
            searchText: undefined,
        }), [accessToken, custAccountId, frameworkPlugins, language, notificationItems, runtimeAccountId]);
        const navigateFromPlugin = (0, hooks_1.useCallback)((url) => {
            const urlObj = new URL(url, window.location.origin);
            const navigationItem = urlObj.origin !== window.location.origin
                ? { labelKey: "", path: urlObj.href }
                : { labelKey: "", pluginName: "unknown", appendPath: urlObj.href.replace(urlObj.origin, "") };
            setIsDrawerOpen(false);
            const handled = navigate(navigationItem)();
            if (!handled && "path" in navigationItem) {
                window.open(navigationItem.path, "_self");
            }
        }, [navigate]);
        const onOpen = (0, hooks_1.useCallback)(() => {
            (0, agent_1.closeAiAgent)();
            (0, analytics_1.trackAnalytics)({ linkName: "Header - Notifications" });
            setIsDrawerOpen(true);
        }, []);
        const onClose = (0, hooks_1.useCallback)(() => {
            setIsDrawerOpen(false);
        }, []);
        (0, hooks_1.useEffect)(() => {
            if (!isDrawerOpen || !notificationCenterUrl || !(notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.path)) {
                return undefined;
            }
            const runtime = (0, FrameworkPluginRuntime_1.getFrameworkPluginRuntime)(runtimeLabel);
            const notificationCenterOrigin = new URL(notificationCenterUrl, window.location.origin).origin;
            const onHandshakeInit = (event) => {
                var _a;
                const data = event.data;
                if ((data === null || data === void 0 ? void 0 : data.label) === runtimeLabel
                    && data.tag === "handshakeInit"
                    && event.origin === notificationCenterOrigin
                    && event.source === ((_a = frameRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
                    alignRuntimeChildWindow(runtime, event.source);
                }
            };
            window.addEventListener("message", onHandshakeInit);
            runtime.init({
                initialContext: runtimeContext,
                messageHandlers: {
                    navigate: navigateFromPlugin,
                    setHeight: height => setWindowHeight(height || "100%"),
                    addRecentActivity: () => undefined,
                    trackAnalytics: analytics_1.trackAnalytics,
                    pluginUnhandledException: error => console.error("[OCC] Notification Center plugin error.", error),
                    handleChangeLanguageRequest: () => undefined,
                    pluginDialogOpenedFunc: () => undefined,
                    refreshService: service => {
                        (0, environment_1.debug)("[OCC] Notification Center refresh service request received", service);
                        if (service === "notification") {
                            refreshNotifications();
                        }
                    },
                    selectAccount: () => {
                        window.dispatchEvent(new CustomEvent("occ:switch-company:select-account-request"));
                    },
                    hideAccount: () => {
                        window.dispatchEvent(new CustomEvent("occ:switch-company:hide-account-request"));
                    },
                },
                options: { keepAliveOnClose: true },
            });
            // Let oj-c-drawer-popup finish moving its layer content before navigating
            // the iframe; otherwise Chrome can start and then abort the initial load.
            let openFrameId;
            openFrameId = window.requestAnimationFrame(() => {
                openFrameId = window.requestAnimationFrame(() => {
                    openFrameId = undefined;
                    runtime.openPlugin({
                        url: notificationCenterUrl,
                        pluginPath: notificationCenterPlugin.path,
                        embedded: { frameId },
                        debug: environment_1.isDebugOn,
                        allowedOrigins,
                    }).catch(error => console.error("[OCC] Exception during opening Notification Center.", error));
                });
            });
            return () => {
                if (openFrameId !== undefined) {
                    window.cancelAnimationFrame(openFrameId);
                }
                window.removeEventListener("message", onHandshakeInit);
                runtime.disposeService();
                setWindowHeight("100%");
            };
        }, [
            allowedOrigins,
            isDrawerOpen,
            navigateFromPlugin,
            notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.path,
            notificationCenterUrl,
            refreshNotifications,
            runtimeContext,
        ]);
        if (!(notificationCenterPlugin === null || notificationCenterPlugin === void 0 ? void 0 : notificationCenterPlugin.url)) {
            return null;
        }
        return ((0, jsx_runtime_1.jsxs)("span", { className: "occ-notification-center", "data-testid": "occNotificationCenterBell", children: [(0, jsx_runtime_1.jsx)("oj-c-button", { id: "notificationBellButton", "data-testid": "notificationBellButton", display: "icons", chroming: "borderless", size: "sm", label: drawerTitle, onojAction: onOpen, children: (0, jsx_runtime_1.jsx)("span", { slot: "startIcon", "data-testid": "notificationBellIcon", className: `oj-ux-ico-notification notification-icon ${(notificationItems === null || notificationItems === void 0 ? void 0 : notificationItems.length) ? "has-notifications" : ""}` }) }), (0, jsx_runtime_1.jsx)("oj-c-drawer-popup", { opened: isDrawerOpen, edge: "end", class: "occ-notification-center-drawer", id: "occNotificationCenterDrawer", "data-testid": "occNotificationCenterDrawer", closeGesture: "none", autoDismiss: "focus-loss", onojClose: onClose, children: (0, jsx_runtime_1.jsxs)("div", { className: "occ-notification-center-drawer-content", children: [(0, jsx_runtime_1.jsxs)("div", { className: "occ-notification-center-drawer-header", children: [(0, jsx_runtime_1.jsx)("span", { className: "oj-typography-subheading-sm", children: drawerTitle }), (0, jsx_runtime_1.jsx)("oj-c-button", { id: "occNotificationCenterCloseButton", "data-testid": "occNotificationCenterCloseButton", display: "icons", chroming: "borderless", size: "sm", label: closeButtonLabel, onojAction: onClose, children: (0, jsx_runtime_1.jsx)("span", { slot: "startIcon", className: "oj-ux-ico-close" }) })] }), isDrawerOpen && ((0, jsx_runtime_1.jsx)("iframe", { ref: frameRef, id: frameId, "data-testid": frameId, title: drawerTitle, className: "occ-notification-center-frame", style: { minHeight: windowHeight }, frameBorder: 0, allow: "local-network", sandbox: "allow-forms allow-popups allow-popups-to-escape-sandbox allow-scripts allow-same-origin allow-downloads allow-modals", allowFullScreen: true }))] }) })] }));
    };
    exports.OccNotificationCenterBell = OccNotificationCenterBell;
});
